import Immutable from 'immutable';
import React from 'react';

import ResourceInput from 'sharedreact/components/ResourceInput';
import type ErrorData from 'sharedreact/data/ErrorData';

type Props<T, S> = {
  id: string,
  className?: string,
  name: string,
  value: T | null,
  emptyValue?: T | null,
  listIcon?: string,
  placeholder?: string,
  noSuggestions?: boolean,
  requestFocus?: boolean,
  disabled?: boolean,
  missingResource?: boolean,
  missingResourceName?: string,
  tooltip?: string,
  validations?: string[],

  format?: (value: T | S | null) => string,
  onApplyNonMatch?: (value: string, event?: React.FocusEvent<HTMLInputElement>) => void,
  onShowResources: (filterString: string | null, name: string) => void,
  onGetSuggestions?: (filterString: string | null, onSuccess: (items: Immutable.List<S>, totalCount: number) => void, onFailure:(errore: ErrorData) => void) => void,
  onChangeValue: (name: string, value: T | S | null, event?: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLUListElement>) => void,
  getInputAddon?: () => React.ReactNode,
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void,
  isEmpty?: (filterString: string) => boolean,
  validate?: (name: string) => void
};

type State<S> = {
  filterString: string,
  waiting: boolean,
  suggestions: Immutable.List<S>,
  totalSuggestionCount: number
};

function _isEmpty (filterString: string): boolean {
  return filterString.length === 0;
}

function _defaultFormat (resource: unknown): string {
  if (resource == null) {
    return '';
  } else if (typeof resource === 'string' || typeof resource === 'number' || typeof resource === 'boolean') {
    return resource + '';
  } else if (typeof resource === 'object' && 'name' in resource && typeof resource.name === 'string') {
    return resource.name;
  }
  throw new Error('Can not format resource, you need to pass a custom format function');
}

type DefaultProps<T, S> = Required<Pick<Props<T, S>, 'placeholder' | 'emptyValue' | 'listIcon' | 'missingResource' |
  'missingResourceName' | 'noSuggestions' | 'validations' | 'isEmpty' | 'format'>>;

type PropsWithDefaults<T, S> = Omit<Props<T, S>, keyof DefaultProps<T, S>> & DefaultProps<T, S>;

class ResourceInputWithState<T, S = T> extends React.Component<Props<T, S>, State<S>> {
  static defaultProps: DefaultProps<unknown, unknown> = {
    placeholder: '',
    emptyValue: null,
    listIcon: 'esko_icon esko_icon-more',
    missingResource: false,
    missingResourceName: 'shared.missing.resource.name.general',
    noSuggestions: false,
    validations: [],
    isEmpty: _isEmpty,
    format: _defaultFormat
  };
  
  constructor (props: Props<T, S>) {
    super(props);

    this.state = {
      filterString: '',
      waiting: false,
      suggestions: Immutable.List(),
      totalSuggestionCount: 0
    };

    this.handleGetSuggestions = this.handleGetSuggestions.bind(this);
    this.handleClearResource = this.handleClearResource.bind(this);
    this.handleSuggestionSelected = this.handleSuggestionSelected.bind(this);
    this.handleRestoreResource = this.handleRestoreResource.bind(this);
    this.handleChangeValue = this.handleChangeValue.bind(this);

    this._onGetSuggestionsSuccess = this._onGetSuggestionsSuccess.bind(this);
    this._onGetSuggestionsFail = this._onGetSuggestionsFail.bind(this);
  }

  override componentDidMount (): void {
    const { format } = (this.props as PropsWithDefaults<T, S>);
    this.setState({filterString: format(this.props.value)});
  }

  updateState (prevProps: Props<T, S>) {
    const { format } = (this.props as PropsWithDefaults<T, S>);
    if (prevProps.value !== this.props.value) {
      this.setState({filterString: format(this.props.value)});
    }
  }

  override componentDidUpdate (prevProps: Readonly<Props<T, S>>, prevState: Readonly<State<S>>): void {
    this.updateState(prevProps);
  }

  _onGetSuggestionsSuccess (items: Immutable.List<S>, totalCount: number): void {
    this.setState({
      suggestions: items,
      totalSuggestionCount: totalCount,
      waiting: false
    });
  }

  _onGetSuggestionsFail (errorData: ErrorData): void {
    this.setState({
      suggestions: Immutable.List(),
      totalSuggestionCount: 0,
      waiting: false
    });
  }

  handleGetSuggestions () {
    this.setState({waiting: true});
    if (this.props.onGetSuggestions != null) {
      this.props.onGetSuggestions(this.state.filterString, this._onGetSuggestionsSuccess, this._onGetSuggestionsFail);
    }
  }

  handleClearResource (event?: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement>) {
    const { format, emptyValue } = (this.props as PropsWithDefaults<T, S>);
    this.setState({filterString: format(emptyValue)});
    this.props.onChangeValue(this.props.name, emptyValue, event);
  }

  handleSuggestionSelected (suggestionIndex: number, event?: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLUListElement> | React.FocusEvent<HTMLInputElement>) {
    const { format } = (this.props as PropsWithDefaults<T, S>);
    const selectedSuggestion: S | undefined = this.state.suggestions.get(suggestionIndex);

    if (selectedSuggestion != null) {
      this.setState({filterString: format(selectedSuggestion)});
      this.props.onChangeValue(this.props.name, selectedSuggestion, event);
    }
  }

  handleRestoreResource (event?: React.FocusEvent<HTMLInputElement>) {
    const { format } = (this.props as PropsWithDefaults<T, S>);
    this.setState({filterString: format(this.props.value)});
  }

  handleChangeValue (name: string, filterString: string, event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({filterString: filterString});
  }

  override render (): React.ReactNode {
    const { id, className, name, placeholder, noSuggestions, requestFocus, disabled,
      missingResource, missingResourceName, validations, validate, tooltip, listIcon, onApplyNonMatch,
      onShowResources, getInputAddon, onBlur, isEmpty, format } = (this.props as PropsWithDefaults<T, S>);

    const { filterString, suggestions,
      totalSuggestionCount, waiting } = this.state;

    const suggestionsStrings: Immutable.List<string> = suggestions.map((suggestion) => format(suggestion));

    return <ResourceInput id={id}
      className={className}
      name={name}
      value={filterString}
      suggestions={suggestionsStrings}
      totalSuggestionCount={totalSuggestionCount}
      waiting={waiting}
      listIcon={listIcon}
      placeholder={placeholder}
      noSuggestions={noSuggestions}
      requestFocus={requestFocus}
      disabled={disabled}
      missingResource={missingResource}
      missingResourceName={missingResourceName}
      tooltip={tooltip}
      validations={validations}
      validate={validate}
      onApplyNonMatch={onApplyNonMatch}
      onChangeValue={this.handleChangeValue}
      onGetSuggestions={this.handleGetSuggestions}
      onSuggestionSelected={this.handleSuggestionSelected}
      onShowResource={onShowResources}
      onClearResource={this.handleClearResource}
      onRestoreResource={this.handleRestoreResource}
      getInputAddon={getInputAddon}
      onBlur={onBlur}
      isEmpty={isEmpty}
    />;
  }
}

export default ResourceInputWithState;
